import { memo, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  analyzeRichText,
  cleanRichTextHref,
  escapeHtml,
  richTextToEditorHtml,
  sanitizeRichTextHtml,
} from '../../../lib/richText'
import { Skeleton } from '../../atoms'
import { RichTextLinkRow } from './RichTextLinkRow'
import { RichTextToolbar, type ToolbarAction } from './RichTextToolbar'

interface RichTextFieldProps {
  id: string
  label: string
  // The stored HTML (or old plain text). Emitted back already sanitized, '' when empty.
  value: string
  onChange: (html: string) => void
  error?: string
  hint?: string
  disabled?: boolean
  skeleton?: boolean
}

// document.execCommand is deprecated but supported by every browser, and it is the one way to
// edit a contenteditable without a library.
const exec = (command: string, arg?: string) => document.execCommand?.(command, false, arg)

const queryState = (command: string): boolean => {
  try {
    return !!document.queryCommandState?.(command)
  } catch {
    return false
  }
}

export const RichTextField = memo(
  ({ id, label, value, onChange, error, hint, disabled, skeleton }: RichTextFieldProps) => {
    const { t } = useTranslation('admin-mobifull')
    const labelId = `${id}-label`
    const hintId = `${id}-hint`
    const linkRowId = `${id}-link`
    const linkErrorId = useId()
    const rootRef = useRef<HTMLDivElement>(null)
    const editorRef = useRef<HTMLDivElement>(null)
    const linkInputRef = useRef<HTMLInputElement>(null)
    // The last value this field emitted or loaded: a different `value` prop is an outside change
    const lastValue = useRef<string | null>(null)
    // The selection last seen inside the editor (the link row takes focus away from it)
    const savedRange = useRef<Range | null>(null)
    const linkTarget = useRef<HTMLAnchorElement | null>(null)
    const [pressed, setPressed] = useState<Partial<Record<ToolbarAction, boolean>>>({})
    const [linkOpen, setLinkOpen] = useState(false)
    const [linkValue, setLinkValue] = useState('')
    const [linkError, setLinkError] = useState('')
    const [editingLink, setEditingLink] = useState(false)

    // Load: an outside value goes into the editor through the allowlist walk (old plain text is
    // escaped into paragraphs). The editor's own edits are not written back, so the caret stays.
    useLayoutEffect(() => {
      const editor = editorRef.current
      if (skeleton || !editor || value === lastValue.current) {
        return
      }
      editor.innerHTML = richTextToEditorHtml(value)
      // Emit the normalized value once when it differs from what came in (old plain text, markup
      // the walk cleans, nothing visible): an untouched Save then sends clean html. lastValue
      // is set first, so the parent's echo of this value is not loaded or emitted again.
      const analysis = analyzeRichText(editor.innerHTML)
      const normalized = analysis.empty ? '' : analysis.html
      lastValue.current = normalized
      if (normalized !== value) {
        onChange(normalized)
      }
    }, [value, skeleton, onChange])

    useEffect(() => {
      if (!skeleton) {
        // <p> instead of <div> for new lines (the walk maps div to p anyway)
        exec('defaultParagraphSeparator', 'p')
      }
    }, [skeleton])

    const emit = useCallback(() => {
      const editor = editorRef.current
      if (!editor) {
        return
      }
      const analysis = analyzeRichText(editor.innerHTML)
      const next = analysis.empty ? '' : analysis.html
      lastValue.current = next
      onChange(next)
    }, [onChange])

    const refreshState = useCallback(() => {
      const editor = editorRef.current
      const selection = window.getSelection?.()
      if (!editor || !selection || selection.rangeCount === 0) {
        return
      }
      if (!editor.contains(selection.anchorNode)) {
        return
      }
      savedRange.current = selection.getRangeAt(0).cloneRange()
      const next = {
        bold: queryState('bold'),
        italic: queryState('italic'),
        ul: queryState('insertUnorderedList'),
        ol: queryState('insertOrderedList'),
      }
      // Same four booleans: keep the old object, so the memoized toolbar does not re-render
      setPressed(prev =>
        prev.bold === next.bold &&
        prev.italic === next.italic &&
        prev.ul === next.ul &&
        prev.ol === next.ol
          ? prev
          : next,
      )
    }, [])

    useEffect(() => {
      document.addEventListener('selectionchange', refreshState)
      return () => document.removeEventListener('selectionchange', refreshState)
    }, [refreshState])

    const restoreSelection = () => {
      editorRef.current?.focus()
      const range = savedRange.current
      const selection = window.getSelection?.()
      if (range && selection && editorRef.current?.contains(range.commonAncestorContainer)) {
        selection.removeAllRanges()
        selection.addRange(range)
      }
    }

    const run = (command: string, arg?: string) => {
      restoreSelection()
      exec(command, arg)
      emit()
      refreshState()
    }

    const closeLink = (refocus: boolean) => {
      setLinkOpen(false)
      setLinkError('')
      setEditingLink(false)
      linkTarget.current = null
      if (refocus) {
        editorRef.current?.focus()
      }
    }

    const openLink = () => {
      // A link around the caret is edited, not duplicated
      const node = savedRange.current?.commonAncestorContainer
      const element = node instanceof Element ? node : (node?.parentElement ?? null)
      const anchor = element?.closest('a') ?? null
      const inside = anchor && editorRef.current?.contains(anchor) ? anchor : null
      linkTarget.current = inside
      setEditingLink(!!inside)
      setLinkValue(inside?.getAttribute('href') ?? '')
      setLinkError('')
      setLinkOpen(true)
    }

    useEffect(() => {
      if (linkOpen) {
        linkInputRef.current?.focus()
      }
    }, [linkOpen])

    const applyLink = () => {
      const href = cleanRichTextHref(linkValue)
      if (href === null) {
        setLinkError(t('richText.linkInvalid'))
        linkInputRef.current?.focus()
        return
      }
      const existing = linkTarget.current
      if (existing) {
        existing.setAttribute('href', href)
        emit()
        closeLink(true)
        return
      }
      restoreSelection()
      const collapsed = window.getSelection?.()?.isCollapsed ?? true
      if (collapsed) {
        // Nothing selected: the address becomes the link text
        exec('insertHTML', `<a href="${escapeHtml(href)}">${escapeHtml(href)}</a>`)
      } else {
        exec('createLink', href)
      }
      emit()
      closeLink(true)
    }

    const removeLink = () => {
      const existing = linkTarget.current
      restoreSelection()
      if (existing) {
        const selection = window.getSelection?.()
        const range = document.createRange()
        range.selectNodeContents(existing)
        selection?.removeAllRanges()
        selection?.addRange(range)
      }
      exec('unlink')
      emit()
      closeLink(true)
    }

    const handleAction = (action: ToolbarAction) => {
      if (action === 'bold') {
        run('bold')
      } else if (action === 'italic') {
        run('italic')
      } else if (action === 'ul') {
        run('insertUnorderedList')
      } else if (action === 'ol') {
        run('insertOrderedList')
      } else if (action === 'hr') {
        run('insertHorizontalRule')
      } else if (linkOpen) {
        closeLink(true)
      } else {
        openLink()
      }
    }

    // A stable callback for the memoized toolbar: it always calls the latest handler
    const actionRef = useRef(handleAction)
    useLayoutEffect(() => {
      actionRef.current = handleAction
    })
    const onAction = useCallback((action: ToolbarAction) => actionRef.current(action), [])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) {
        return
      }
      const key = e.key.toLowerCase()
      if (key === 'b' || key === 'i') {
        e.preventDefault()
        run(key === 'b' ? 'bold' : 'italic')
      }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault()
      const html = e.clipboardData.getData('text/html')
      const text = e.clipboardData.getData('text/plain')
      const clean = html ? sanitizeRichTextHtml(html) : ''
      if (clean) {
        exec('insertHTML', clean)
      } else if (text) {
        exec('insertText', text)
      }
      emit()
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      // A drop could bring markup that never went through the walk: text only
      e.preventDefault()
      const text = e.dataTransfer.getData('text/plain')
      if (text) {
        exec('insertText', text)
        emit()
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      // Leaving the field: the live editor shows exactly what was emitted (b -> strong, ...)
      if (rootRef.current?.contains(e.relatedTarget as Node | null)) {
        return
      }
      const editor = editorRef.current
      if (editor) {
        const clean = sanitizeRichTextHtml(editor.innerHTML)
        if (clean !== editor.innerHTML) {
          editor.innerHTML = clean
        }
      }
    }

    if (skeleton) {
      return (
        <div className="form-group">
          <span className="form-label">{label}</span>
          <Skeleton shape="textarea" />
        </div>
      )
    }

    const describedBy = [hint ? hintId : '', error ? `${id}-error` : ''].filter(Boolean).join(' ')

    return (
      <div className="form-group rich-text" ref={rootRef}>
        {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: a mouse shortcut like a <label>; the keyboard reaches the editor by Tab */}
        <span
          className="form-label"
          id={labelId}
          onClick={() => {
            if (!disabled) {
              editorRef.current?.focus()
            }
          }}>
          {label}
        </span>
        <div className={`rich-text__box${error ? ' rich-text__box--error' : ''}`}>
          <RichTextToolbar
            pressed={pressed}
            linkOpen={linkOpen}
            disabled={disabled}
            onAction={onAction}
            linkControlsId={linkRowId}
          />
          {linkOpen ? (
            <RichTextLinkRow
              id={linkRowId}
              value={linkValue}
              error={linkError}
              errorId={linkErrorId}
              editing={editingLink}
              inputRef={linkInputRef}
              onChange={value => {
                setLinkValue(value)
                setLinkError('')
              }}
              onApply={applyLink}
              onRemove={removeLink}
              onCancel={() => closeLink(true)}
            />
          ) : null}
          {/* biome-ignore lint/a11y/useSemanticElements: contenteditable is the editor */}
          <div
            ref={editorRef}
            id={id}
            className="rich-text__content"
            role="textbox"
            tabIndex={0}
            aria-multiline="true"
            aria-labelledby={labelId}
            aria-describedby={describedBy || undefined}
            aria-invalid={!!error}
            aria-disabled={disabled || undefined}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={emit}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onDrop={handleDrop}
            onBlur={handleBlur}
          />
        </div>
        {hint ? (
          <small className="form-hint" id={hintId}>
            {hint}
          </small>
        ) : null}
        {error ? (
          <p className="error-text" id={`${id}-error`}>
            {error}
          </p>
        ) : null}
      </div>
    )
  },
)

RichTextField.displayName = 'RichTextField'
